/* eslint-disable max-lines */
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  debounceTime,
  delay,
  filter,
  map,
  repeat,
  switchMap,
  tap,
} from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { BehaviorSubject, EMPTY, of, throwError } from 'rxjs';
import { InteractionBarStateService } from '../../services/interaction-bar-state.service';
import { PresignedFileUploadService } from '../../services/presigned-file-upload.service';
import { PrimeCommitmentsApiService } from '../../services/prime-commitments-api.service';
import { viewProjectSelectors } from '../view-project/view-project.selectors';
import { viewProjectActions } from '../view-project/view-project.actions';
import {
  primeContractActions,
  primeChangesSidebarActions,
  pccoActions,
  corDetailsActions,
  corListingActions,
} from './prime-commitments.actions';
import { CORLogMetadata, PrimeChangeOrderResponse } from './prime-commitments.types';
import {
  primeDetailsSelectors,
  primeSelectors,
} from '@app/store/prime-commitments/prime-commitments.selectors';
import { COR_LISTING_PER_PAGE_BASE } from '@app/store/prime-commitments/prime-commitments.constants';
import { ChangeActionsStore } from '@app/store/change-actions/change-actions.store';
import { primeSummaryActions } from '@app/pages/webapp/projects/view-project/add-panel-view/prime-commitment-view/prime-contract-summary.actions';
import { NotificationV2Service } from '@app/notification-v2.service';
import { DriveApiService } from '@app/services/drive-api.service';
import { DriveService } from '@app/services/drive.service';

@Injectable()
export class PrimeCommitmentsEffects {
  private readonly store = inject(Store);
  private readonly actions = inject(Actions);
  private readonly notif = inject(NotificationV2Service);
  private readonly presignedFileUploadService = inject(PresignedFileUploadService);
  private readonly primeApiService = inject(PrimeCommitmentsApiService);
  private readonly interactionBar = inject(InteractionBarStateService);
  private readonly changeActionsStore = inject(ChangeActionsStore);
  private readonly driveApiService = inject(DriveApiService);
  private readonly driveService = inject(DriveService);

  static isPccoOpen$ = new BehaviorSubject<boolean>(false);
  static isPrimeInvoiceOpen$ = new BehaviorSubject<boolean>(false);

  onChangesSidebarOpen = createEffect(() => {
    return this.actions.pipe(
      ofType(primeChangesSidebarActions.onChangeActionSidebarOpen),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([_, projectId]) => {
        return this.primeApiService.getNextCORNumber(projectId).pipe(
          map((corNumberRequest) =>
            primeChangesSidebarActions.nextCORNumberLoadedSuccessfully({
              nextCORNumber: corNumberRequest.next_id,
            }),
          ),
          catchError((err) => {
            this.notif.showError(err?.error?.message || 'Could not load next COR number');
            return of(
              primeChangesSidebarActions.nextCORNumberLoadFailed({
                error: err,
              }),
            );
          }),
        );
      }),
    );
  });

  onPCCOSidebarOpen = createEffect(() => {
    return this.actions.pipe(
      ofType(pccoActions.onPCCOSidebarOpen),
      tap(() => {
        setTimeout(() => {
          this.changeActionsStore.loadCORs(true);
        }, 100);
      }),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([_, projectId]) => this.primeApiService.getNextPCCONumber(projectId)),
      map((nextIdResponse) =>
        pccoActions.nextPCCONumberLoaded({
          nextPCCONumber: nextIdResponse.next_id,
        }),
      ),
      repeat(),
    );
  });

  onChangesSidebarOpenLoadPrimeContract = createEffect(() => {
    return this.actions.pipe(
      ofType(
        viewProjectActions.selectedProjectChanged,
        viewProjectActions.refreshNeeded,
        // primeChangesActions.onCORDetailsAccess,
        // primeChangesActions.onChangeActionSidebarOpen,
        // primeContractActions.primeContractSaved,
        // primePCODetailsSidebarActions.onPCODetailsAccessed,
        // primeSummaryActions.onPrimeContractDelete,
      ),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([_, projectId]) => {
        return this.primeApiService.loadPrimeContractByProjectId(projectId);
      }),
      map((primeContract) =>
        primeContractActions.onPrimeContractLoaded({
          response: primeContract,
        }),
      ),
      catchError((_) => {
        this.notif.showError('This project does not have a prime contract.');
        return of(
          primeContractActions.onPrimeContractLoaded({
            response: null,
          }),
        );
      }),
      repeat(),
    );
  });

  onEditPCCO = createEffect(() => {
    return this.actions.pipe(
      ofType(pccoActions.onPrimeChangeOrderEdit),
      switchMap((action) => {
        return this.primeApiService.loadPrimeChangeOrderById(action.pcco_id);
      }),
      map((pcco) =>
        pccoActions.onPrimeChangeOrderLoaded({
          response: pcco,
        }),
      ),
      catchError((_) => {
        this.notif.showError('Could not load Prime Change Order');
        return of(
          pccoActions.onPrimeChangeOrderLoaded({
            response: null,
          }),
        );
      }),
      repeat(),
    );
  });

  onCORLogDelete = createEffect(() => {
    return this.actions.pipe(
      ofType(corDetailsActions.onDeleteCOR),
      switchMap((action) => {
        return this.primeApiService.deleteCORLogItem(action.id);
      }),
      tap(() => {
        this.notif.showSuccess('Successfully deleted');
      }),
      delay(1000),
      map(() => {
        this.interactionBar.close();
        return corDetailsActions.onCORDeletedSuccessfully();
      }),
      catchError((error) => {
        this.notif.showError(error?.error?.message || 'An error occurred while deleting PCO');
        return of(corDetailsActions.onCORFailedToDelete());
      }),
      repeat(),
    );
  });

  onPCCONumberChange = createEffect(() => {
    return this.actions.pipe(
      ofType(pccoActions.onPCCONumberChange),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([action, projectId]) => {
        return this.primeApiService.isPCCONumberUnique(
          projectId,
          action.number,
          action.resource_id,
        );
      }),
      map((_) => pccoActions.PCCONumberValidated({ isValid: true })),
      catchError((_) => {
        return of(pccoActions.PCCONumberValidated({ isValid: false }));
      }),
      repeat(),
    );
  });

  onCORNumberChange = createEffect(() => {
    return this.actions.pipe(
      ofType(primeChangesSidebarActions.onCORNumberChange),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([action, projectId]) => {
        return this.primeApiService.isCORNumberUnique(projectId, action.number);
      }),
      map((_) => primeChangesSidebarActions.CORNumberValidated({ isValid: true })),
      catchError((_) => {
        return of(primeChangesSidebarActions.CORNumberValidated({ isValid: false }));
      }),
      repeat(),
    );
  });

  onPrimeContractCreate$ = createEffect(() => {
    return this.actions.pipe(
      ofType(primeContractActions.onPrimeContractSubmit),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([action, projectId]) => {
        const hash = this.notif.showLoading();
        return this.primeApiService.createPrimeContract(projectId, action.primeContract).pipe(
          tap(() => {
            this.notif.close(hash);
          }),
          map((response) => {
            this.notif.showSuccess('Prime contract created successfully');
            return primeContractActions.primeContractSavedSuccessfully({
              primeContractResponse: response,
              model: action.primeContract,
            });
          }),
          catchError((error) => {
            console.error(error);
            this.notif.showError(
              "Something went wrong. Couldn't create prime contract. " +
                (error?.error?.message ?? ''),
            );

            return of(primeContractActions.onFailedToSubmitPrimeContract());
          }),
        );
      }),
    );
  });

  onPrimeContractUpdate$ = createEffect(() => {
    return this.actions.pipe(
      ofType(primeContractActions.onPrimeContractUpdate),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([action, projectId]) => {
        const hash = this.notif.showLoading();
        return this.primeApiService.updatePrimeContract(projectId, action.primeContract).pipe(
          tap(() => {
            this.notif.close(hash);
          }),
          map((response) => {
            this.notif.showSuccess('Prime contract saved successfully');
            return primeContractActions.primeContractSavedSuccessfully({
              primeContractResponse: response,
              model: action.primeContract,
            });
          }),
          catchError((error) => {
            console.error(error);
            this.notif.showError(
              "Something went wrong. Couldn't update prime contract. " +
                (error?.error?.message ?? ''),
            );

            return of(primeContractActions.onFailedToSubmitPrimeContract());
          }),
        );
      }),
    );
  });

  manageFilesAfterPrimeContractSave$ = createEffect(() => {
    return this.actions.pipe(
      ofType(primeContractActions.primeContractSavedSuccessfully),
      switchMap((action) => {
        return this.presignedFileUploadService
          .handleUploadAndDelete(action?.model?.files, { pc_id: action.primeContractResponse.id })
          .pipe(
            map((responses) => {
              this.interactionBar.close();
              if (responses.some((res) => res?.error)) {
                console.error('Error managing prime contract files', responses);
                this.notif.showError('An error occurred while updating Prime Contract files.');
                return primeContractActions.onFailedToManagePrimeContractFiles();
              }
              return primeContractActions.primeContractFilesManagedSuccessfully();
            }),
          );
      }),
    );
  });

  onPrimeContractDelete = createEffect(() => {
    return this.actions.pipe(
      ofType(primeSummaryActions.onPrimeContractDelete),
      concatLatestFrom(() => this.store.select(primeSelectors.selectPrimeContract)),
      switchMap(([_, primeContract]) => {
        if (!primeContract?.data?.id) {
          console.error('Prime contract ID is missing when deleting prime contract');
          return throwError(() => new Error('An error occurred while deleting prime contract'));
        }
        return this.primeApiService.deletePrimeContract(primeContract?.data?.id).pipe(
          catchError((error) => {
            this.notif.showError(
              error?.error?.message || 'An error occurred while deleting prime contract',
            );
            console.error(error);
            return of(null);
          }),
        );
      }),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      map(([response, projectId]) => {
        if (!response) {
          return primeSummaryActions.onFailedToDeletePrimeContract();
        }
        if (!projectId) {
          console.warn('No project ID found when deleting prime contract');
          return primeSummaryActions.onFailedToDeletePrimeContract();
        }
        this.notif.showSuccess('Prime contract deleted successfully');
        return primeSummaryActions.onPrimeContractSuccessfullyDeleted({ projectId });
      }),
    );
  });

  primeContractDownloadFiled$ = createEffect(() => {
    return this.actions.pipe(
      ofType(primeSummaryActions.downloadPCFilesSelected),
      concatLatestFrom(() => this.store.select(primeSelectors.selectPrimeContract)),
      switchMap(([_, primeContract]) => {
        if (!primeContract?.data?.files || !primeContract?.data?.id) {
          this.notif.showError('No files found to download');
          return EMPTY.pipe(map(() => null));
        }
        return this.driveService
          .downloadMultipleFilesAsZip(
            primeContract.data.files,
            `${primeContract.data.title}_attachments`,
          )
          .pipe(
            map(() => {
              return null;
            }),
            catchError((error) => {
              this.notif.showError(
                'An error occurred while downloading files. ' + (error?.error?.message || ''),
              );
              return EMPTY;
            }),
          );
      }),
    );
  });

  onPCCOSubmit$ = createEffect(() => {
    return this.actions.pipe(
      ofType(pccoActions.onPCCOSubmit),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([action, projectId]) =>
        this.primeApiService.savePCCO(projectId, action.model).pipe(
          map((response: PrimeChangeOrderResponse) => {
            this.notif.showSuccess('Prime Change Order saved successfully');
            return pccoActions.onPCCOSaved({ response, model: action.model });
          }),
          catchError((error) => {
            console.error(error);
            const errorMessage = error?.error?.message || 'An error occurred while submitting';
            this.notif.showError(errorMessage);
            return of(pccoActions.onFailedToSavePCCO());
          }),
        ),
      ),
    );
  });

  uploadPCCOFiles$ = createEffect(() => {
    return this.actions.pipe(
      ofType(pccoActions.onPCCOSaved),
      switchMap(({ response, model }) => {
        return this.presignedFileUploadService
          .handleUploadAndDelete(model?.generalData?.files, { pcco_id: response.id })
          .pipe(
            map((responses) => {
              this.interactionBar.close();
              if (responses.some((res) => res?.error)) {
                console.error('Error managing PCCO files', responses);
                this.notif.showError('An error occurred while updating PCCO files');
                return pccoActions.onPCCOFilesUploadFailed();
              }
              return pccoActions.onPCCOFilesUploadedSuccessfully();
            }),
          );
      }),
    );
  });

  onPCCODelete = createEffect(() => {
    return this.actions.pipe(
      ofType(pccoActions.onPCCODelete),
      switchMap((action) => {
        return this.primeApiService.deletePCCO(action.pccoId).pipe(
          tap(() => {
            this.notif.showSuccess('Prime Change Order deleted successfully');
          }),
          map(() => pccoActions.onPCCODeletedSuccessfully({ pccoId: action.pccoId })),
          catchError((error) => {
            console.error(error);
            const errorMessage = error?.error?.message || 'An error occurred while deleting';
            this.notif.showError(errorMessage);
            return of(pccoActions.onFailedToDeletePCCO());
          }),
        );
      }),
    );
  });

  onCORSubmit = createEffect(() => {
    return this.actions.pipe(
      ofType(primeChangesSidebarActions.onCORSubmit),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([action, projectId]) => {
        const request$ = action.model.generalData.id
          ? this.primeApiService.updateCOR(projectId, action.model)
          : this.primeApiService.createCOR(projectId, action.model);
        return request$.pipe(
          map((response) => {
            this.notif.showSuccess(
              `Change order ${action.model.generalData.id ? 'updated' : 'saved'} successfully`,
            );
            return primeChangesSidebarActions.onCORSaved({
              response: response,
              model: action.model,
            });
          }),
          catchError((error) => {
            console.error(error);
            const errorMessage =
              error?.error?.message || 'An error occurred while submitting potential change order';
            this.notif.showError(errorMessage);
            return of(primeChangesSidebarActions.onFailedToSaveCOR());
          }),
        );
      }),
    );
  });

  manageFilesAfterCorSave$ = createEffect(() => {
    return this.actions.pipe(
      ofType(primeChangesSidebarActions.onCORSaved),
      switchMap(({ response, model }) => {
        return this.presignedFileUploadService
          .handleUploadAndDelete(model?.generalData?.files, { cor_id: response.id })
          .pipe(
            map((responses) => {
              this.interactionBar.close();
              if (responses.some((res) => res?.error)) {
                console.error('Error managing COR files', responses);
                this.notif.showError('An error occurred while updating COR files');
                return primeChangesSidebarActions.onCORFilesUploadFailed;
              }
              return primeChangesSidebarActions.onCORFilesUploadedSuccessfully();
            }),
          );
      }),
    );
  });

  onCORDetailsSync = createEffect(() => {
    return this.actions.pipe(
      ofType(corDetailsActions.onCORLocalStateChanged),
      switchMap((action) => {
        return this.primeApiService.updateCORDetails(action.COR).pipe(
          map(() => {
            this.notif.showSuccess(`Change order request updated`);
            return corDetailsActions.onCORDetailsSaved();
          }),
          catchError((error) => {
            console.error(error);
            const errorMessage = error?.error?.message || 'An error occurred while updating';
            this.notif.showError(errorMessage);
            return of(corDetailsActions.onFailedToSaveCORDetails());
          }),
        );
      }),
    );
  });

  CORListingLoad = createEffect(() => {
    return this.actions.pipe(
      ofType(
        corListingActions.onCORListingAccess,
        // viewProjectActions.selectedProjectChanged,
        // pccoActions.onPCCOSidebarOpen,
        corDetailsActions.onCORDetailsSidebarClosed,
        primeChangesSidebarActions.onCORSaved,
        corDetailsActions.onCORDeletedSuccessfully,
        corListingActions.onCORStatusFilterChange,
        corListingActions.onCORListingSearch,
        corListingActions.onCORListingNextPageNeeded,
        corListingActions.onCORSortingChange,
      ),
      filter((action) => {
        // if editChangeActionTriggered is true, then don't load PCOs because an another interaction bar was opened
        return !(
          action.type === corDetailsActions.onCORDetailsSidebarClosed.type &&
          action.editChangeActionTriggered
        );
      }),
      debounceTime(200),
      concatLatestFrom(() => [
        this.store.select(viewProjectSelectors.selectProjectId),
        this.store.select(primeSelectors.selectCORListingInfo),
      ]),
      switchMap(([action, projectId, corLstingInfo]) => {
        const requestMetadata: CORLogMetadata = {
          page: 1,
          perPage: COR_LISTING_PER_PAGE_BASE,
          statuses: corLstingInfo.statuses,
          search: corLstingInfo.search,
          sortBy: corLstingInfo.sortBy,
          sortDirection: corLstingInfo.sortDirection,
          isAssigned: null, // we don't care if the COR is assigned or not in the listing
        };

        if (
          action.type === corListingActions.onCORListingAccess.type &&
          corLstingInfo.allCORLogs.length > 0
        ) {
          return EMPTY;
        }
        if (action.type === corListingActions.onCORListingNextPageNeeded.type) {
          requestMetadata.page = corLstingInfo.lastPage + 1;
          if (corLstingInfo.lastPageEmpty) {
            return EMPTY;
          }
        }
        if (action.type === corListingActions.onCORStatusFilterChange.type) {
          requestMetadata.statuses = action.statuses;
        }
        if (action.type === corListingActions.onCORListingSearch.type) {
          requestMetadata.search = action.search;
        }

        return this.primeApiService.getAllCORs(projectId, requestMetadata).pipe(
          map((response) =>
            corListingActions.CORListingLogsLoaded({ CORs: response, requestMetadata }),
          ),
          catchError((error) => {
            this.notif.showError(error?.error?.message || 'An error occurred while loading CORs');
            return of(corListingActions.onFailedToLoadCORLogs());
          }),
        );
      }),
    );
  });

  onLoadCORDetails = createEffect(() => {
    return this.actions.pipe(
      ofType(corDetailsActions.onCORDetailsAccess),
      switchMap((action) => {
        return this.primeApiService.loadCORDetails(action.id);
      }),
      map((response) => corDetailsActions.CORDetailsLoaded({ response: response })),
      catchError((error) => {
        this.notif.showError(
          error?.error?.message || 'An error occurred while loading COR details',
        );
        return of(corDetailsActions.onFailedToLoadCORDetails());
      }),
      repeat(),
    );
  });

  primeContractViewNeeded = createEffect(() => {
    return this.actions.pipe(
      // todo: update summary after every PC, PCCO, COR, PCO change...
      ofType(
        primeSummaryActions.loadPage,
        viewProjectActions.selectedProjectChanged,
        viewProjectActions.refreshNeeded,
        primeSummaryActions.onPrimeContractSuccessfullyDeleted,
        pccoActions.onPCCODeletedSuccessfully,
      ),
      debounceTime(100),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([action, projectId]) => {
        if (!projectId) {
          console.error('No project ID found when loading prime contract summary');
          return EMPTY;
        }
        return this.primeApiService.getPrimeContractViewSummary(projectId).pipe(
          map((response) => {
            return primeSummaryActions.onPrimeContractSummaryViewLoaded({ summary: response });
          }),
        );
      }),
      catchError((error) => {
        if (error.error?.message) {
          this.notif.showError(error.error.message);
        } else {
          this.notif.showError('An error occurred while loading prime contract summary');
        }
        return of(primeSummaryActions.onFailedToLoadPrimeContractSummary());
      }),
    );
  });

  markPrimeContractAsSubmittedEffect = createEffect(() => {
    return this.actions.pipe(
      ofType(primeSummaryActions.sendPrimeContract),
      concatLatestFrom(() => [this.store.select(primeDetailsSelectors.selectPrimeContractSummary)]),
      switchMap(([action, primeContract]) => {
        return this.primeApiService.submitPrimeContract(primeContract.id).pipe(
          map((response) => {
            this.notif.showSuccess('Prime contract submitted.');
            return primeSummaryActions.onPrimeContractSentSuccessfully();
          }),
        );
      }),
      catchError((error) => {
        if (error.error?.message) {
          this.notif.showError(error.error.message);
        } else {
          this.notif.showError('An error occurred while submitting prime contract.');
        }
        return of(primeSummaryActions.onFailedToSendPrimeContract());
      }),
    );
  });
}
